import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSocket } from "../context/SocketContext";
import TicketExtendedModal from "../compoment/modals/TicketExtendedModal";
import { TICKET_EXTENDED_EVENT } from "../constants/events";
import { clearBrowserStorage } from "../utils/storage";
import { getPaymentSubscriptionUserId } from "../utils/ticket";

const TICKET_EXTENDED_REDIRECT_MS = 3_000;

const TicketSessionManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentSocket, paymentConnected } = useSocket();
  const [showTicketExtendedModal, setShowTicketExtendedModal] = useState(false);
  const ticketExtendedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const ticketExtendedHandledRef = useRef(false);

  const clearTicketExtendedTimer = useCallback(() => {
    if (ticketExtendedTimerRef.current) {
      clearTimeout(ticketExtendedTimerRef.current);
      ticketExtendedTimerRef.current = null;
    }
  }, []);

  const handleTicketExtended = useCallback(() => {
    if (ticketExtendedHandledRef.current) return;
    ticketExtendedHandledRef.current = true;

    window.dispatchEvent(new CustomEvent(TICKET_EXTENDED_EVENT));
    setShowTicketExtendedModal(true);
    clearTicketExtendedTimer();

    ticketExtendedTimerRef.current = setTimeout(() => {
      setShowTicketExtendedModal(false);
      ticketExtendedHandledRef.current = false;
      void clearBrowserStorage();
      navigate("/", { replace: true });
    }, TICKET_EXTENDED_REDIRECT_MS);
  }, [clearTicketExtendedTimer, navigate]);

  useEffect(() => {
    if (!paymentSocket || !paymentConnected) return;

    const userId = getPaymentSubscriptionUserId();
    if (!userId) return;

    paymentSocket.emit("subscribeToPaymentStatus", { userId });

    const onTicketTimeUpdated = () => {
      handleTicketExtended();
    };

    const onPaymentStatusUpdate = () => {
      handleTicketExtended();
    };

    paymentSocket.on("ticketTimeUpdated", onTicketTimeUpdated);
    paymentSocket.on("paymentStatusUpdate", onPaymentStatusUpdate);

    return () => {
      paymentSocket.off("ticketTimeUpdated", onTicketTimeUpdated);
      paymentSocket.off("paymentStatusUpdate", onPaymentStatusUpdate);
    };
  }, [paymentSocket, paymentConnected, handleTicketExtended, location.pathname]);

  useEffect(() => {
    return () => {
      clearTicketExtendedTimer();
    };
  }, [clearTicketExtendedTimer]);

  return (
    <TicketExtendedModal
      show={showTicketExtendedModal}
      onHide={() => setShowTicketExtendedModal(false)}
    />
  );
};

export default TicketSessionManager;
